import Input from "../../../../../../../../components/FormikForm/Input";
import Button from "../../../../../../../../components/Button";
import { setToZeroTimeZone, inputDateTimeLocal } from "../../../../../../../../helpers/dates-hanlder";
import { dateSpliter } from "../../../../../../../../helpers/dates-hanlder";
import WaitingListFields from "../WaitingListFields";
import StyledCheckbox from "../StyledCheckbox";
import {
  Container,
  CheckboxLabel,
  CheckboxText,
  SimpleCheckboxLabel,
  SimpleCheckboxLabelText,
  SummaryWrapper,
  SummaryText
} from "./styles";
import { useMemo } from "react";

interface OpenSubscriptionsProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (e: any) => void;
  handleBlur: (e: any) => void;
  setFieldValue: (field: string, value: any) => void;
  includeClosing: boolean;
  setIncludeClosing: (value: boolean) => void;
  openNow: boolean;
  setOpenNow: (value: boolean) => void;
  showSummaryOnly?: boolean;
  onEditScheduledOpening?: () => void;
  onCancelScheduledOpening?: () => void;
}

const OpenSubscriptions = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  includeClosing,
  setIncludeClosing,
  openNow,
  setOpenNow,
  showSummaryOnly = false,
  onEditScheduledOpening,
  onCancelScheduledOpening
}: OpenSubscriptionsProps) => {

  const isMembership = useMemo(() => values?.type === 'membership', [values?.type]);

  // Handler para convertir fecha local a UTC antes de guardar
  const handleDateChange = (fieldName: string) => (e: any) => {
    e.stopPropagation();
    const localDateTime = e.target.value;
    if (localDateTime) {
      const utcDateTime = setToZeroTimeZone(localDateTime);
      setFieldValue(fieldName, utcDateTime);
    } else {
      setFieldValue(fieldName, '');
    }
  };

  // Convertir fechas UTC a local para mostrar en los inputs
  const getLocalDateTimeValue = (utcDate: string | null) => {
    if (!utcDate) return '';
    try {
      return inputDateTimeLocal(utcDate);
    } catch (error) {
      console.error('Error converting UTC to local:', error);
      return '';
    }
  };

  // Generar texto explicativo basado en las opciones seleccionadas
  const getSummaryText = () => {
    let text = '';

    // Evaluar apertura
    if (openNow) {
      text += 'Las inscripciones se <strong>abrirán</strong> cuando guardes los cambios';
    } else if (values?.subscriptions_opening_date) {
      const openingDate = dateSpliter(values.subscriptions_opening_date);
      text += `Las inscripciones se abrirán el <strong>${openingDate.day.number} de ${openingDate.month.name.large} del ${openingDate.year} a las ${openingDate.hours}:${openingDate.minutes}</strong>`;
    } else {
      text += `Indica cuando quieres <strong>abrir las inscripciones</strong> de tu ${isMembership ? 'membresía' : 'taller'}`;
    }

    // Evaluar cierre si está marcado
    if (includeClosing) {
      if (values?.subscriptions_closing_date) {
        const closingDate = dateSpliter(values.subscriptions_closing_date);
        text += ` y se cerrarán el <strong>${closingDate.day.number} de ${closingDate.month.name.large} del ${closingDate.year} a las ${closingDate.hours}:${closingDate.minutes}</strong>`;

        // Evaluar lista de espera
        if (values?.waiting_list_external_url) {
          text += '. Después del cierre, las personas serán redirigidas al link que configuraste';
        }
      } else {
        text += '. Debe indicar una <strong>fecha de cierre</strong> para completar la programación';
      }
    }


    text += '.';
    return text;
  };

  // Generar texto específico para cuando showSummaryOnly es true
  const getSummaryOnlyText = () => {
    let text = '<strong>Actualmente, las inscripciones están cerradas</strong>. ';

    if (values?.subscriptions_opening_date) {
      const openingDate = dateSpliter(values.subscriptions_opening_date);
      text += `Se abrirán el <strong>${openingDate.day.number} de ${openingDate.month.name.large} de ${openingDate.year} a las ${openingDate.hours}:${openingDate.minutes}</strong>`;
    }

    if (values?.subscriptions_closing_date) {
      const closingDate = dateSpliter(values.subscriptions_closing_date);
      text += ` y se cerrarán el <strong>${closingDate.day.number} de ${closingDate.month.name.large} de ${closingDate.year} a las ${closingDate.hours}:${closingDate.minutes}</strong>`;
    }

    if (values?.waiting_list_external_url) {
      text += '. Después del cierre, las personas serán redirigidas al link que configuraste.';
    } else {
      text += '.';
    }

    return text;
  };

  const summaryText = showSummaryOnly ? getSummaryOnlyText() : getSummaryText();

  if (showSummaryOnly) {
    return (
      <Container>
        <SummaryWrapper style={{ margin: 0 }}>
          <SummaryText dangerouslySetInnerHTML={{ __html: summaryText }} />
        </SummaryWrapper>

        <div style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
          <Button
            options={{ type: "outline", skin: "purple", size: "md" }}
            onClick={() => {
              onEditScheduledOpening?.();
            }}
          >
            Editar fechas
          </Button>

          <Button
            options={{ type: "filled", skin: "purple", size: "md" }}
            onClick={() => {
              onCancelScheduledOpening?.();
            }}
          >
            Cancelar y dejar cerradas
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <CheckboxLabel checked={openNow}>
        <StyledCheckbox
          id="open-now"
          checked={openNow}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            setOpenNow(e.target.checked);
            setFieldValue('openNow', e.target.checked);
            if (e.target.checked) {
              setFieldValue('subscriptions_opening_date', '');
            }
          }}
        />
        <CheckboxText checked={openNow} htmlFor="open-now">
          Abrir ahora
        </CheckboxText>
      </CheckboxLabel>

      <div style={{ height: 16 }} />

      <div onClick={(e) => e.stopPropagation()}>
        <Input
          name="subscriptions_opening_date"
          type="datetime-local"
          error={errors?.subscriptions_opening_date}
          touched={touched?.subscriptions_opening_date}
          value={getLocalDateTimeValue(values?.subscriptions_opening_date)}
          onChange={handleDateChange('subscriptions_opening_date')}
          onBlur={handleBlur}
          disabled={openNow}
          options={{
            explainText: "Elegí día y horario para abrir las inscripciones.",
            label: "Fecha de apertura",
            marginBottom: 0,
          }}
        />
      </div>

      <div style={{ height: 24 }} />

      <SimpleCheckboxLabel hasMargin={includeClosing}>
        <StyledCheckbox
          id="include-closing"
          checked={includeClosing}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            setIncludeClosing(e.target.checked);
            setFieldValue('includeClosing', e.target.checked);
            if (!e.target.checked) {
              setFieldValue('subscriptions_closing_date', '');
            }
          }}
        />
        <SimpleCheckboxLabelText htmlFor="include-closing">
          ¿Quieres elegir una fecha de cierre?
        </SimpleCheckboxLabelText>
      </SimpleCheckboxLabel>

      <p style={{
        margin: ' 8px 0px',
        fontSize: '14px',
        lineHeight: '17px',
        color: 'rgb(113, 113, 113)',
      }}>Elegí que día y horario se cerraran las inscripciones.</p>

      {includeClosing && (
        <>
          <div onClick={(e) => e.stopPropagation()}>
            <Input
              name="subscriptions_closing_date"
              type="datetime-local"
              error={errors?.subscriptions_closing_date}
              touched={touched?.subscriptions_closing_date}
              value={getLocalDateTimeValue(values?.subscriptions_closing_date)}
              onChange={handleDateChange('subscriptions_closing_date')}
              onBlur={handleBlur}
              options={{
                label: "",
                marginBottom: 0
              }}
            />
          </div>

          <div style={{ height: 24 }} />

          <WaitingListFields
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
          />
        </>
      )}

      <SummaryWrapper>
        <SummaryText dangerouslySetInnerHTML={{ __html: summaryText }} />
      </SummaryWrapper>
    </Container>
  );
};

export default OpenSubscriptions;

